import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Target, Plus, CheckCircle, Send, Clock, MessageCircle } from 'lucide-react';
import DictationInput from '../components/DictationInput';
import VoiceRecorder from '../components/VoiceRecorder';
import { storeReflection } from '../lib/aiReflections';

export default function GoalsPage() {
  const { user, axiosAuth } = useAuth();
  const [goals, setGoals] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(null);
  const [requestForm, setRequestForm] = useState({ requested_progress: 0, notes: '' });
  const [requestOriginal, setRequestOriginal] = useState('');
  const [form, setForm] = useState({ title: '', description: '', target_date: '', category: 'recovery' });
  const [formOriginal, setFormOriginal] = useState('');
  const api = axiosAuth();

  const load = () => {
    api.get('/goals').then(r => setGoals(r.data)).catch(() => {});
    if (user.role === 'client') {
      api.get('/goals/progress-requests?status=pending').then(r => setMyRequests(r.data)).catch(() => {});
    }
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.title) return toast.error('Title is required');
    try {
      await api.post('/goals', form);
      toast.success('Goal created!');
      if (form.description?.trim()) {
        await storeReflection(api, {
          original_text: formOriginal || form.description,
          refined_text: form.description,
          context: 'goal_create',
          task_name: form.title,
          task_description: 'Goal creation reflection',
          category: form.category,
          source: 'goals'
        });
      }
      setOpen(false);
      setForm({ title: '', description: '', target_date: '', category: 'recovery' });
      setFormOriginal('');
      load();
    } catch { toast.error('Failed to create goal'); }
  };

  const handleRequestProgress = async (goalId) => {
    try {
      await api.post(`/goals/${goalId}/request-progress`, {
        goal_id: goalId,
        requested_progress: requestForm.requested_progress,
        notes: requestForm.notes
      });
      toast.success('Progress request sent to your peer specialist!');
      if (requestForm.notes?.trim()) {
        await storeReflection(api, {
          original_text: requestOriginal || requestForm.notes,
          refined_text: requestForm.notes,
          context: 'goal_progress',
          task_name: `Progress request: ${goals.find(g => g.id === goalId)?.title || 'Goal'}`,
          task_description: 'Goal progress update from resident',
          category: goals.find(g => g.id === goalId)?.category || 'recovery',
          source: 'goals'
        });
      }
      setRequestOpen(null);
      setRequestForm({ requested_progress: 0, notes: '' });
      setRequestOriginal('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send request');
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      await api.put(`/goals/${id}`, data);
      toast.success('Goal updated!');
      load();
    } catch (e) {
      if (e.response?.status === 403) {
        toast.info(e.response.data.detail);
      } else {
        toast.error('Failed to update');
      }
    }
  };

  const catColors = { recovery: 'bg-teal-50 text-teal-700', housing: 'bg-sky-50 text-sky-700', employment: 'bg-amber-50 text-amber-700', education: 'bg-violet-50 text-violet-700', health: 'bg-emerald-50 text-emerald-700' };

  const hasPendingRequest = (goalId) => myRequests.some(r => r.goal_id === goalId);

  return (
    <div className="space-y-6" data-testid="goals-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Goals</h1><p className="text-muted-foreground text-sm mt-1">Track your progress</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="new-goal-button"><Plus size={16} /> New Goal</Button></DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Create Goal</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-1.5"><Label className="text-sm">Title</Label>
                <div className="flex gap-1"><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="What's your goal?" data-testid="goal-title-input" className="flex-1" /><VoiceRecorder size="icon" onTranscription={t => setForm(p => ({...p, title: t}))} /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-sm">Description</Label>
                <DictationInput
                  value={form.description}
                  onChange={(cleaned, original) => { setForm(p => ({...p, description: cleaned})); setFormOriginal(original || cleaned); }}
                  placeholder="Describe your goal"
                  rows={2}
                  promptContext={{
                    context: 'goal',
                    taskName: form.title || 'Goal description',
                    taskDescription: 'Describe the goal and why it matters',
                    category: form.category
                  }}
                  data-testid="goal-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-sm">Category</Label>
                  <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recovery">Recovery</SelectItem>
                      <SelectItem value="housing">Housing</SelectItem>
                      <SelectItem value="employment">Employment</SelectItem>
                      <SelectItem value="education">Education</SelectItem>
                      <SelectItem value="health">Health</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label className="text-sm">Target Date</Label><Input type="date" value={form.target_date} onChange={e => setForm({...form, target_date: e.target.value})} /></div>
              </div>
              <Button onClick={handleCreate} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="create-goal-button">Create Goal</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {user.role === 'client' && (
        <div className="bg-sky-50 border border-sky-200 rounded-2xl p-3 text-sm text-sky-800">
          <p className="font-medium flex items-center gap-2"><MessageCircle size={14} /> Goal progress requires peer specialist approval</p>
          <p className="text-xs text-sky-600 mt-0.5">Request progress updates and your peer specialist will review and approve them.</p>
        </div>
      )}

      <div className="grid gap-3">
        {goals.length > 0 ? goals.map(g => (
          <Card key={g.id} className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0 mt-0.5">
                  {g.status === 'completed' ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <Target className="w-5 h-5 text-teal-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className={`font-medium ${g.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>{g.title}</p>
                    <Badge className={catColors[g.category] || 'bg-muted'} variant="secondary">{g.category}</Badge>
                    {hasPendingRequest(g.id) && <Badge className="bg-amber-50 text-amber-700 text-xs gap-1"><Clock size={10} /> Pending Review</Badge>}
                  </div>
                  {g.description && <p className="text-sm text-muted-foreground mb-2">{g.description}</p>}
                  <div className="text-xs text-muted-foreground mb-2 flex gap-3 flex-wrap">
                    {g.created_at && <span>Started: {new Date(g.created_at).toLocaleDateString()}</span>}
                    {g.target_date && <span>Target: {new Date(g.target_date).toLocaleDateString()}</span>}
                    <span>Status: {g.status}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Progress value={g.progress || 0} className="flex-1 h-2" />
                    <span className="text-xs text-muted-foreground w-10 text-right">{g.progress || 0}%</span>
                  </div>
                  {g.status !== 'completed' && user.role === 'client' && (
                    <div className="flex gap-2 mt-3">
                      {!hasPendingRequest(g.id) ? (
                        <Dialog open={requestOpen === g.id} onOpenChange={v => { setRequestOpen(v ? g.id : null); if (!v) setRequestForm({ requested_progress: 0, notes: '' }); }}>
                          <DialogTrigger asChild>
                            <Button size="sm" className="rounded-full text-xs h-7 bg-teal-600 hover:bg-teal-700 text-white gap-1" data-testid={`request-progress-${g.id}`}>
                              <Send size={12} /> Request Progress
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="rounded-2xl">
                            <DialogHeader><DialogTitle>Request Progress Update</DialogTitle></DialogHeader>
                            <div className="space-y-4 pt-2">
                              <p className="text-sm text-muted-foreground">Request a progress update for "<strong>{g.title}</strong>". Your peer specialist will review and approve.</p>
                              <div className="space-y-1.5">
                                <Label className="text-sm">Requested Progress (%)</Label>
                                <Input type="number" min={g.progress || 0} max={100} value={requestForm.requested_progress || (g.progress || 0) + 10} onChange={e => setRequestForm({...requestForm, requested_progress: parseInt(e.target.value) || 0})} />
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-sm">What progress have you made?</Label>
                                <DictationInput
                                  value={requestForm.notes}
                                  onChange={(cleaned, original) => { setRequestForm(p => ({...p, notes: cleaned})); setRequestOriginal(original || cleaned); }}
                                  placeholder="Describe what you've accomplished..."
                                  rows={3}
                                  promptContext={{
                                    context: 'goal_progress',
                                    taskName: `Progress update: ${g.title}`,
                                    taskDescription: 'Share what changed and how it felt',
                                    category: g.category
                                  }}
                                  data-testid="goal-progress-notes"
                                />
                              </div>
                              <Button onClick={() => handleRequestProgress(g.id)} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="submit-progress-request">
                                <Send size={14} /> Submit Request
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      ) : (
                        <Badge className="bg-amber-50 text-amber-700 text-xs gap-1"><Clock size={10} /> Awaiting Peer Review</Badge>
                      )}
                    </div>
                  )}
                  {g.status !== 'completed' && user.role !== 'client' && (
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => handleUpdate(g.id, { progress: Math.min((g.progress || 0) + 10, 100) })} data-testid={`goal-progress-${g.id}`}>+ Progress</Button>
                      <Button size="sm" variant="outline" className="rounded-full text-xs h-7 text-emerald-600" onClick={() => handleUpdate(g.id, { status: 'completed', progress: 100 })}>Mark Complete</Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )) : (
          <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-8 text-center">
            <Target className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No goals yet. Set your first goal!</p>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
